import type { SpeakOptions, SpeakSource } from '../contracts';
import type { SpeakableSentence } from './speakable';

export type ReplayEntry = {
  key: string;
  source: SpeakSource;
  options: SpeakOptions;
  sentences: SpeakableSentence[];
};
export const messageIdFor = (source: SpeakSource) =>
  source.kind === 'confirmation' ? source.ref.id : source.messageId;
export function sourceKey(source: SpeakSource, options: SpeakOptions): string {
  const ref =
    source.kind === 'confirmation'
      ? [
          source.kind,
          source.ref.kind,
          source.ref.id,
          source.digest,
          source.messageId,
          source.line ?? 'summary',
        ]
      : [
          source.kind,
          source.threadId,
          source.messageId,
          source.kind === 'stored_message' ? source.revision : null,
          source.kind === 'message' ? source.channel : null,
          source.kind === 'stored_message' ? source.format : null,
        ];
  return JSON.stringify([ref, options.rate]);
}
const sizeOf = (entry: ReplayEntry) => JSON.stringify(entry).length * 2;

export function createReplayCache(maxReplies = 5, maxBytes = 20 * 1024 * 1024) {
  const entries = new Map<string, ReplayEntry>();
  let bytes = 0;
  const remove = (key: string) => {
    const old = entries.get(key);
    if (old) bytes -= sizeOf(old);
    entries.delete(key);
  };
  return {
    put(entry: ReplayEntry) {
      remove(entry.key);
      const size = sizeOf(entry);
      if (size > maxBytes) return;
      entries.set(entry.key, entry);
      bytes += size;
      while (entries.size > maxReplies || bytes > maxBytes) remove(entries.keys().next().value!);
    },
    get(messageId: string) {
      const hit = [...entries.values()]
        .reverse()
        .find((entry) => messageIdFor(entry.source) === messageId);
      if (hit) {
        entries.delete(hit.key);
        entries.set(hit.key, hit);
      }
      return hit;
    },
    clear() {
      entries.clear();
      bytes = 0;
    },
  };
}
